// Saved for later: the cart drawer's second tab, kept in the Swish (Wishlist King) wishlist.
//
// "Save for later" on a cart line saves that variant to the wishlist, then removes the line.
// The tab lists the whole wishlist: lines saved from the cart first (with the quantity and
// properties they had, so "Move to cart" puts back what was there), then products hearted
// around the site. Swish only stores product + variant, so the quantity and properties live
// in this browser's localStorage, keyed by variant id. Without them an item still shows,
// just under Wishlist.
(() => {
  if (customElements.get('saved-for-later')) return;

  const STORAGE_KEY = 'mcs:saved-for-later';
  const SWISH_TIMEOUT = 15000;

  // ---- Swish ----------------------------------------------------------------------------

  // The Swish app embed sets window.WishlistKing once its core module has loaded.
  let swishPromise;
  function swish() {
    if (!swishPromise) {
      swishPromise = new Promise((resolve, reject) => {
        const started = Date.now();
        const poll = () => {
          if (window.WishlistKing?.state) return resolve(window.WishlistKing);
          if (Date.now() - started > SWISH_TIMEOUT) return reject(new Error('Swish wishlist is not available'));
          setTimeout(poll, 150);
        };
        poll();
      });
      // A later click tries again rather than reusing the failure.
      swishPromise.catch(() => { swishPromise = undefined; });
    }
    return swishPromise;
  }

  // Swish can't take two changes at once: one that starts mid-save throws, and one that
  // starts while the last is re-syncing is ignored (returns undefined). So changes run one
  // at a time, and an ignored one is retried until Swish accepts it.
  let swishQueue = Promise.resolve();
  function swishAction(method, params) {
    const run = swishQueue.then(async () => {
      const app = await swish();
      const started = Date.now();
      for (;;) {
        const result = await app[method](params);
        if (result) return result;
        if (Date.now() - started > SWISH_TIMEOUT) throw new Error(`Swish ${method} did not complete`);
        await new Promise((resolve) => setTimeout(resolve, 250));
      }
    });
    swishQueue = run.catch(() => {});
    return run;
  }

  // ---- Quantities and properties of lines saved from the cart ----------------------------

  function readSaved() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function writeSaved(saved) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
    } catch (error) {
      // Private mode or storage blocked: the item is still in the wishlist.
    }
  }

  function remember(variantId, quantity, properties) {
    const saved = readSaved();
    const previous = saved[variantId];
    saved[variantId] = {
      quantity: (previous?.quantity || 0) + quantity,
      properties: { ...(previous?.properties || {}), ...properties },
      savedAt: Date.now()
    };
    writeSaved(saved);
  }

  function forget(variantId) {
    const saved = readSaved();
    if (!(variantId in saved)) return;
    delete saved[variantId];
    writeSaved(saved);
  }

  // ---- "Save for later" on a cart line ---------------------------------------------------

  // Lines being saved: a re-render can show the link again before the save finishes,
  // and a second click would double the stored quantity.
  const savingLines = new Set();

  // Delegated: the drawer's line items are re-rendered on every cart change.
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-save-for-later]');
    if (!button) return;
    event.preventDefault();

    const line = button.getAttribute('data-index');
    if (savingLines.has(line)) return;
    savingLines.add(line);
    // The drawer re-renders on every cart change (another save finishing, say), which
    // replaces this line's <cart-items>, so look it up again when it's needed.
    const cartItems = button.closest('cart-items');
    const currentCartItems = () => document.getElementById(`CartDrawer-Item-${line}`)?.closest('cart-items') || cartItems;
    const variantId = parseInt(button.getAttribute('data-variant-id'), 10);
    const quantity = parseInt(button.getAttribute('data-quantity'), 10) || 1;
    cartItems?.enableLoading(line);

    // Recorded first: Swish re-renders the tab as soon as the item lands, and the entry is
    // what files it under "Saved for later" rather than "Wishlist".
    const previous = readSaved()[variantId];
    remember(variantId, quantity, publicProperties(button.getAttribute('data-properties')));

    try {
      const result = await swishAction('saveForLater', {
        productHandle: button.getAttribute('data-handle'),
        variantId
      });
      if (!result.id) throw new Error('Swish did not save the item');

      currentCartItems()?.updateQuantity(line, 0);
    } catch (error) {
      console.error(error);
      const saved = readSaved();
      if (previous) saved[variantId] = previous;
      else delete saved[variantId];
      writeSaved(saved);
      currentCartItems()?.disableLoading(line);
      const currentButton = document.getElementById(`CartDrawer-Item-${line}`)?.querySelector('[data-save-for-later]');
      showLineError(currentButton || button, 'Couldn’t save this item. Please try again.');
    } finally {
      savingLines.delete(line);
    }
  });

  // Underscore properties are the theme's own bookkeeping (bundles and the like).
  function publicProperties(json) {
    try {
      const properties = JSON.parse(json || '{}') || {};
      return Object.fromEntries(Object.entries(properties).filter(([key, value]) => !key.startsWith('_') && value !== ''));
    } catch (error) {
      return {};
    }
  }

  function showLineError(button, message) {
    const container = button.closest('.cart-line-actions');
    if (!container) return;
    let error = container.querySelector('.cart-line-actions__error');
    if (!error) {
      error = document.createElement('p');
      error.className = 'cart-line-actions__error';
      error.setAttribute('role', 'alert');
      container.appendChild(error);
    }
    error.textContent = message;
  }

  // ---- The Saved for later tab -----------------------------------------------------------

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[char]);

  const money = (cents) => theme.Currency.formatMoney(Math.round(cents), theme.settings.moneyFormat);

  const sizedImage = (url, width) => url ? `${url}${url.includes('?') ? '&' : '?'}width=${width}` : '';

  class SavedForLater extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      const panel = this.closest('.drawer__panel');
      this.countElement = panel && document.querySelector(`[aria-controls="${panel.id}"] .saved-for-later__count`);
      this.items = [];
      this.busy = new Set();

      this.addEventListener('click', this.onClick.bind(this));

      swish()
        .then(async (app) => {
          // First load straight from the API so an empty wishlist still renders (the
          // observable below only reports changes), then follow Swish's own updates:
          // hearts toggled elsewhere on the page and our own saves and removals.
          const { wishlist } = await app.apis.wishlist.loadWishlist({ wishlistId: 'mine' });
          this.pruneSaved(wishlist);
          this.render(wishlist);
          app.state.observeWishlist({ wishlistId: 'mine' }).subscribe((next) => this.render(next));
        })
        .catch((error) => {
          console.error(error);
          this.renderMessage('Saved items aren’t available right now.', 'Please refresh the page to try again.');
        });
    }

    // Forget quantities for items that have left the wishlist (removed on the wishlist page,
    // or on another device).
    pruneSaved(wishlist) {
      const inWishlist = new Set(wishlist.items.map((item) => String(item.variantId)));
      const saved = readSaved();
      const kept = Object.fromEntries(Object.entries(saved).filter(([variantId]) => inWishlist.has(variantId)));
      if (Object.keys(kept).length !== Object.keys(saved).length) writeSaved(kept);
    }

    render(wishlist) {
      // Swish's optimistic updates add a placeholder product (no title) until it reloads.
      this.items = (wishlist?.items || []).filter((item) => item.product?.title);

      const saved = readSaved();
      const savedItems = this.items.filter((item) => item.variantId && saved[item.variantId]);
      const wishlistItems = this.items.filter((item) => !savedItems.includes(item));

      this.updateCount(this.items.length);

      if (this.items.length === 0) {
        this.renderMessage(
          'Nothing saved yet',
          'Use Save for later on anything in your cart, or tap the heart on a product, and it will wait for you here.'
        );
        return;
      }

      this.innerHTML = `
        <div class="drawer__scrollable relative flex flex-col gap-8 grow shrink">
          ${this.renderGroup('Saved for later', savedItems, saved)}
          ${this.renderGroup('Wishlist', wishlistItems, saved)}
          <a class="saved-for-later__all link text-sm" href="${escapeHtml(this.getAttribute('data-wishlist-url'))}">View full wishlist</a>
        </div>
      `;
    }

    renderGroup(heading, items, saved) {
      if (items.length === 0) return '';
      return `
        <section class="saved-for-later__group grid gap-4">
          <h3 class="saved-for-later__heading text-sm font-medium leading-tight">${heading} <span class="text-opacity">(${items.length})</span></h3>
          <ul class="horizontal-products grid" role="list">
            ${items.map((item) => this.renderItem(item, saved[item.variantId])).join('')}
          </ul>
        </section>
      `;
    }

    renderItem(item, savedEntry) {
      const product = item.product;
      const variants = product.variants || [];
      const variant = variants.find((candidate) => candidate.id === item.variantId)
        || (variants.length === 1 ? variants[0] : null);

      const url = variant && variants.length > 1 ? `${product.url}?variant=${variant.id}` : product.url;
      const image = variant?.featured_image?.src || product.featured_image;
      const variantTitle = variant && variants.length > 1 ? variant.title : '';

      let price = '';
      if (variant) price = money(variant.price);
      else if (product.price_varies) price = `From ${money(product.price_min)}`;
      else if (typeof product.price_min === 'number') price = money(product.price_min);

      const details = [];
      if (variantTitle) details.push(escapeHtml(variantTitle));
      Object.entries(savedEntry?.properties || {}).forEach(([key, value]) => details.push(`${escapeHtml(key)}: ${escapeHtml(value)}`));
      if (savedEntry && savedEntry.quantity > 1) details.push(`Qty ${savedEntry.quantity}`);

      let action;
      if (variant && variant.available) {
        action = `<button type="button" class="button button--primary button--small" is="hover-button" data-action="add">
            <span class="btn-fill" data-fill></span>
            <span class="btn-text">${savedEntry ? 'Move to cart' : 'Add to cart'}</span>
          </button>`;
      } else if (variant) {
        action = '<span class="button button--primary button--small" disabled="disabled"><span class="btn-text">Sold out</span></span>';
      } else {
        action = `<a class="button button--primary button--small" is="hover-link" href="${escapeHtml(product.url)}">
            <span class="btn-fill" data-fill></span>
            <span class="btn-text">Choose options</span>
          </a>`;
      }

      const ratio = this.getAttribute('data-image-ratio') || 'portrait';
      const contain = this.hasAttribute('data-image-contain') ? ' media--contain' : '';

      return `
        <li class="horizontal-product saved-for-later__item flex items-start gap-4 md:gap-6" data-item-id="${escapeHtml(item.id)}">
          <a class="horizontal-product__media media media--${escapeHtml(ratio)}${contain} relative overflow-hidden shrink-0" href="${escapeHtml(url)}" tabindex="-1">
            ${image ? `<img src="${escapeHtml(sizedImage(image, 192))}" srcset="${escapeHtml(sizedImage(image, 144))} 144w, ${escapeHtml(sizedImage(image, 192))} 192w, ${escapeHtml(sizedImage(image, 288))} 288w" sizes="96px" alt="${escapeHtml(product.title)}" loading="lazy" width="192" height="192">` : ''}
          </a>
          <div class="horizontal-product__details grow flex flex-col justify-start gap-1d5">
            <a href="${escapeHtml(url)}" class="horizontal-product__title reversed-link font-medium text-base leading-tight">${escapeHtml(product.title)}</a>
            ${details.length ? `<p class="text-xs text-opacity leading-tight">${details.join(' · ')}</p>` : ''}
            ${price ? `<div class="price text-sm">${price}</div>` : ''}
            <p class="saved-for-later__error text-xs leading-tight" role="alert" hidden></p>
          </div>
          <div class="horizontal-product__quantity saved-for-later__actions align-self-stretch shrink-0 text-sm flex flex-col items-end justify-between gap-3">
            ${action}
            <button type="button" class="link text-xs" data-action="remove" aria-label="Remove ${escapeHtml(product.title)} from saved items">Remove</button>
          </div>
        </li>
      `;
    }

    renderMessage(title, message) {
      this.innerHTML = `
        <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
          <div class="drawer__empty grid gap-5 md:gap-8">
            <p class="drawer__empty-text h2 heading leading-none tracking-tight">${escapeHtml(title)}</p>
            <div class="drawer__empty-message text-sm md:text-base leading-tight">${escapeHtml(message)}</div>
          </div>
        </div>
      `;
    }

    updateCount(count) {
      if (!this.countElement) return;
      this.countElement.textContent = count;
      this.countElement.hidden = count === 0;
    }

    async onClick(event) {
      const control = event.target.closest('[data-action]');
      if (!control) return;
      const row = control.closest('[data-item-id]');
      const item = this.items.find((candidate) => candidate.id === row?.getAttribute('data-item-id'));
      if (!item || this.busy.has(item.id)) return;

      event.preventDefault();
      this.busy.add(item.id);
      row.classList.add('is-busy');
      this.setError(row, '');

      const adding = control.getAttribute('data-action') === 'add';
      try {
        if (adding) {
          await this.addToCart(item);
        } else {
          await this.remove(item);
        }

        // That was the last one: show the cart (the tab list's first tab) rather than an empty list.
        if (!this.items.some((other) => other.id !== item.id)) {
          this.closest('cart-drawer')?.tabList?.reset();
        }
      } catch (error) {
        console.error('Saved for later:', error);
        const fallback = adding ? 'Couldn’t add this to your cart. Please try again.' : 'Couldn’t remove this. Please try again.';
        this.setError(row, error.userMessage || fallback);
      } finally {
        this.busy.delete(item.id);
        row.classList.remove('is-busy');
      }
    }

    async addToCart(item) {
      const variants = item.product.variants || [];
      const variant = variants.find((candidate) => candidate.id === item.variantId) || variants[0];
      const savedEntry = readSaved()[item.variantId];

      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));

      const response = await fetch(theme.routes.cart_add_url, {
        ...theme.utils.fetchConfig('javascript'),
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: savedEntry?.quantity || 1, properties: savedEntry?.properties || {} }],
          sections,
          sections_url: window.location.pathname
        })
      });
      const state = await response.json().catch(() => null);
      if (!response.ok || !state || state.status) {
        // Shopify's own reason (sold out, a B2B minimum quantity...) is worth showing as is.
        const reason = state?.description || state?.message;
        const error = new Error(`Cart add failed (${response.status}): ${reason || 'no JSON body'}`);
        if (reason && typeof reason === 'string') error.userMessage = reason;
        throw error;
      }

      // The item is in the cart from here on, so a problem refreshing the drawer (or in
      // anything else listening for cart changes) is logged rather than reported as a failure.
      try {
        const cart = await (await fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET'))).json();
        cart.sections = state.sections;
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'saved-for-later', productVariantId: variant.id, cart });
      } catch (error) {
        console.error('Saved for later: added to cart, but refreshing the cart failed', error);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
      }

      // Moving it to the cart takes it off the list, whether it was saved from the cart or hearted.
      forget(item.variantId);
      await swishAction('removeWishlistItem', { wishlistItemId: item.id });
    }

    async remove(item) {
      await swishAction('removeWishlistItem', { wishlistItemId: item.id });
      forget(item.variantId);
    }

    setError(row, message) {
      const error = row.querySelector('.saved-for-later__error');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    }
  }

  customElements.define('saved-for-later', SavedForLater);
})();
